import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { IntelligenceModels } from '@models/intelligence.model';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { routerSegments } from '@consts/routes';
import { api } from '@consts/url.const';
import { SharedActions } from '@states/shared/shared.action-types';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { createDashboardsResultTableConfig } from './dashboards-result-table.util';
import { DashboardDetailComponent } from './dashboard-detail/dashboard-detail.component';

@Component({
  selector: 'app-dashboards-result',
  standalone: true,
  imports: [CommonModule, TranslatePipe, UiDatasetTableComponent, DashboardDetailComponent],
  templateUrl: './dashboards-result.component.html',
  styleUrls: ['./dashboards-result.component.scss'],
})
export class DashboardsResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  /** Read directly from the service — self-updates without any parent binding. */
  protected readonly response = computed<
    IntelligenceModels.Dashboards.Response | IntelligenceModels.Dashboards.DetailResponse | null
  >(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'dashboards') return null;
    return result.data as
      | IntelligenceModels.Dashboards.Response
      | IntelligenceModels.Dashboards.DetailResponse;
  });

  protected readonly dashboards = computed<IntelligenceModels.Dashboards.ListItem[]>(() => {
    const data = this.response();
    return data && 'dashboards' in data ? data.dashboards : [];
  });

  protected readonly detail = computed<IntelligenceModels.Dashboards.Detail | null>(() => {
    const data = this.response();
    return data && 'dashboard' in data ? data.dashboard : null;
  });

  protected readonly loadingDashboardId = signal<number | null>(null);

  protected readonly tableConfig = computed<DatasetTableConfig<IntelligenceModels.Dashboards.ListItem>>(
    () => createDashboardsResultTableConfig({
      onRowClick: (dashboard) => this.openDashboard(dashboard),
    }),
  );

  constructor() {
    // Publish this view's actions into the shared Cmd+K context bar; clear them
    // when this component unmounts (result type switches away from dashboards).
    // Edit is exposed unconditionally, matching the current Dashboards page.
    effect(() => {
      const detail = this.detail();
      this.resultsService.setHeaderActions(
        detail
          ? [
              {
                icon: 'ui-icon-edit',
                tooltip: 'DASHBOARDS_RESULT.EDIT',
                onClick: () => this.openInNewTab([routerSegments.edit, detail.id]),
              },
              {
                icon: 'ui-icon-new-tab',
                tooltip: 'DASHBOARDS_RESULT.OPEN_FULL_VIEW',
                onClick: () => this.openInNewTab([routerSegments.view, detail.id]),
              },
            ]
          : [
              {
                icon: 'ui-icon-plus',
                tooltip: 'DASHBOARDS_RESULT.CREATE',
                onClick: () => this.openInNewTab([routerSegments.create]),
              },
              {
                icon: 'ui-icon-new-tab',
                tooltip: 'DASHBOARDS_RESULT.OPEN_PAGE',
                onClick: () => this.openInNewTab([routerSegments.main]),
              },
            ],
      );
    });
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected async openDashboard(dashboard: IntelligenceModels.Dashboards.ListItem): Promise<void> {
    if (this.loadingDashboardId() !== null) return;
    this.loadingDashboardId.set(dashboard.id);
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.Dashboards.DetailResponse>(
          `${api.intelligence.dashboards}/${dashboard.id}`,
        ).pipe(takeUntilDestroyed(this.destroyRef)),
        { defaultValue: null },
      );
      if (!response || !this.isDashboardListActive()) return;
      // Use overlay so this drill-down does not insert a new history entry
      // and break the sequential message↔history mapping.
      this.resultsService.setOverlay(response, 'dashboards', response.dashboard.name);
    } catch {
      if (!this.isDashboardListActive()) return;
      this.store.dispatch(
        SharedActions.showMessage({
          error: this.translateService.instant('DASHBOARDS_RESULT.LOAD_ERROR'),
        }),
      );
    } finally {
      this.loadingDashboardId.set(null);
    }
  }

  /**
   * Keep stale-response protection without relying on object identity: the
   * result service may replace an equivalent history snapshot while the
   * request is in flight.
   */
  private isDashboardListActive(): boolean {
    const current = this.resultsService.current();
    return current?.type === 'dashboards' && !!current.data && 'dashboards' in current.data;
  }

  /** Open the target dashboards route in a new tab so the current Cmd+K session stays open. */
  private openInNewTab(segments: (string | number)[]): void {
    const tree = this.router.createUrlTree(['/', routerSegments.dashboards, ...segments]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
